
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { GlobalContext } from 'context/userContext';
import { DrawerHeader } from '@components/drawer-header';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Select,
    CircularProgress,
    Input,
    Paper,
    useTheme,
    Grid,
} from '@mui/material';
import { API_URL, AxiosInstance } from '@service/axios-instance';
import DataTable, { TableColumn } from 'react-data-table-component';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

import {
    getEquipaments,
    getSectors,
    listFirmwares,
    uploadFirmware,
    assignFirmwareToDevice,
    forceFirmwareUpdate,
    Equipament,
    FirmwareFile
} from './api';


export function FirmwareAssignmentPage() {
    const { operation } = useContext(GlobalContext);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
    const [progress, setProgress] = useState<Record<string, number>>({});

    const [equipaments, setEquipaments] = useState<Equipament[]>([]);
    const [firmwares, setFirmwares] = useState<FirmwareFile[]>([]);
    const [sectors, setSectors] = useState<string[]>([]);
    const [heartbeats, setHeartbeats] = useState<Record<string, string>>({});

    const [selectedSector, setSelectedSector] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [firmwareFilter, setFirmwareFilter] = useState('');
    const [idFilter, setIdFilter] = useState('');
    const [apelidoFilter, setApelidoFilter] = useState('');
    const [tidFilter, setTidFilter] = useState('');

    // Bulk selection
    const [bulkFirmwareVersion, setBulkFirmwareVersion] = useState('');

    // Per-row changes: deviceId -> firmwareVersion
    const [changes, setChanges] = useState<Record<string, string>>({});

    // File Upload
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const dbReady = !!operation;

    const loadData = useCallback(async () => {
        if (!operation) return;
        setLoading(true);
        setError(null);
        try {
            const [eqs, fws, secs] = await Promise.all([
                getEquipaments(operation, {}),
                listFirmwares(operation),
                getSectors(operation),
            ]);
            setEquipaments(eqs);
            setFirmwares(fws);
            setSectors(secs);
            setChanges({}); // Reset changes on reload
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? 'Falha ao carregar dados');
        } finally {
            setLoading(false);
        }
    }, [operation]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const sse = new EventSource(API_URL + 'sse/stream');
        sse.addEventListener('equipaments', (e: any) => {
            try {
                const data = JSON.parse(e.data);
                if (data.db && data.db !== operation) return;
            } catch (err) { }

            // Silent refresh of equipments data
            if (operation) {
                getEquipaments(operation, {}).then(setEquipaments).catch(console.error);
            }
        });
        
        sse.addEventListener('fw_progress', (e: any) => {
            try {
                const data = JSON.parse(e.data);
                if (data.db && data.db !== operation) return;
                if (data.device_id && data.percentage !== undefined) {
                    setProgress(prev => ({
                        ...prev,
                        [data.device_id]: data.percentage
                    }));
                }
            } catch (err) { }
        });

        return () => { sse.close(); };
    }, [operation]);

    useEffect(() => {
        if (!operation || equipaments.length === 0) return;
        const validIds = equipaments.map(e => e.id).filter(Boolean);
        if (validIds.length === 0) return;

        AxiosInstance.post(`/heartbeats/get_last_heartbeats_per_id?db=${operation}`, {
            match_filters_dict: { id: validIds }
        }, {
            withCredentials: true
        }).then(res => {
            const hbMap: Record<string, string> = {};
            (res.data || []).forEach((hb: any) => {
                hbMap[hb.id] = hb.stamp;
            });
            setHeartbeats(hbMap);
        }).catch(err => console.error("Error fetching heartbeats", err));
    }, [equipaments, operation]);


    const filteredEquipaments = useMemo(() => {
        return equipaments.filter(eq => {
            if (selectedSector && eq.setor !== selectedSector) return false;
            if (typeFilter && !eq.tipo?.toLowerCase().includes(typeFilter.toLowerCase())) return false;
            if (firmwareFilter && eq.actual_firmware_version !== firmwareFilter) return false;
            if (idFilter && !eq.id?.toLowerCase().includes(idFilter.toLowerCase())) return false;
            if (apelidoFilter && !eq.apelido?.toLowerCase().includes(apelidoFilter.toLowerCase())) return false;
            if (tidFilter && !String(eq.tid || '').toLowerCase().includes(tidFilter.toLowerCase())) return false;
            return true;
        });
    }, [equipaments, selectedSector, typeFilter, firmwareFilter, idFilter, apelidoFilter, tidFilter]);

    const handleFirmwareChange = (deviceId: string, firmwareVersion: string) => {
        setChanges(prev => ({
            ...prev,
            [deviceId]: firmwareVersion
        }));
    };

    const handleBulkApply = () => {
        const newChanges = { ...changes };
        filteredEquipaments.forEach(eq => {
            newChanges[eq.id] = bulkFirmwareVersion;
        });
        setChanges(newChanges);
    };

    const handleForceUpdate = async (deviceId: string) => {
        if (!operation) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            await forceFirmwareUpdate(operation, deviceId);
            setSuccess(`Atualização forçada solicitada para o equipamento ${deviceId}.`);
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? 'Erro ao forçar atualização');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveChanges = async () => {
        if (!operation) return;

        const deviceIdsToUpdate = Object.keys(changes);
        if (deviceIdsToUpdate.length === 0) {
            setSuccess("Nenhuma alteração para salvar.");
            return;
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const promises = deviceIdsToUpdate
                .map(deviceId => {
                    const fwVersion = changes[deviceId];
                    return assignFirmwareToDevice(operation, deviceId, fwVersion);
                });

            if (promises.length === 0) {
                setSuccess("Nenhuma atribuição válida.");
                setSaving(false);
                return;
            }

            await Promise.all(promises);

            setSuccess(`${deviceIdsToUpdate.length} equipamento(s) atualizado(s) com sucesso!`);
            loadData();
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? 'Erro ao salvar alterações');
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshFirmwares = async () => {
        if (!operation) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            import('./api').then(async ({ refreshActualFirmwares }) => {
                const res = await refreshActualFirmwares(operation);
                setSuccess(`${res.count} firmware(s) atualizado(s) com dados de login recentes.`);
                loadData();
            });
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? 'Erro ao atualizar firmwares');
        } finally {
            setSaving(false);
        }
    };

    const handleClearStatus = async () => {
        if (!operation) return;
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            import('./api').then(async ({ clearFirmwareStatus }) => {
                await clearFirmwareStatus(operation);
                setSuccess(`Status de request de firmware limpos com sucesso.`);
                loadData();
            });
        } catch (e: any) {
            console.error(e);
            setError(e?.message ?? 'Erro ao limpar status');
        } finally {
            setSaving(false);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        setUploadSuccess(null);
        setError(null);
        try {
            await uploadFirmware(selectedFile);
            setUploadSuccess(`Arquivo ${selectedFile.name} enviado com sucesso!`);
            setSelectedFile(null);
            // Reload firmwares list
            if (operation) {
                const fws = await listFirmwares(operation);
                setFirmwares(fws);
            }
        } catch (e: any) {
            console.error(e);
            setError("Erro ao enviar arquivo: " + (e?.response?.data?.message || e?.message));
        } finally {
            setUploading(false);
        }
    };

    const exportToCSV = () => {
        const headers = ['ID', 'Apelido', 'TID', 'Tipo', 'Setor', 'Último Heartbeat', 'Firmware Atual', 'Ultimo Request', 'Status', 'Firmware Atribuído'];
        const csvRows = [headers.join(',')];

        filteredEquipaments.forEach(eq => {
            const hb = heartbeats[eq.id];
            let hbFormatted = 'Sem Registro';
            if (hb) {
                const d = new Date(hb);
                if (!isNaN(d.getTime())) {
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = String(d.getFullYear()).slice(-2);
                    const hours = String(d.getHours()).padStart(2, '0');
                    const minutes = String(d.getMinutes()).padStart(2, '0');
                    hbFormatted = `${day}/${month}/${year} ${hours}:${minutes}`;
                } else {
                    hbFormatted = hb;
                }
            }

            const reqTime = eq.last_fw_request_time ? new Date(eq.last_fw_request_time * 1000).toLocaleString() : '-';
            
            let status = eq.last_fw_request_status || '-';
            if (eq.actual_firmware_version && eq.firmware_version && eq.actual_firmware_version === eq.firmware_version) {
                status = 'OK (Updated)';
            } else if (status === 'DOWNLOADING') {
                if (progress[eq.id] !== undefined) {
                    status = `DOWNLOADING ${progress[eq.id]}%`;
                }
                const now = Math.floor(Date.now() / 1000);
                if (eq.last_fw_request_time && (now - eq.last_fw_request_time > 600)) {
                    status = 'FAILED (Timeout)';
                }
            }

            const assigned = changes[eq.id] !== undefined ? changes[eq.id] : (eq.firmware_version || "");

            const row = [
                eq.id || '',
                eq.apelido || '-',
                eq.tid || '',
                eq.tipo || '',
                eq.setor || '',
                hbFormatted,
                eq.actual_firmware_version || '-',
                reqTime,
                status,
                assigned
            ];

            const escapedRow = row.map(cell => `"${String(cell).replace(/"/g, '""')}"`);
            csvRows.push(escapedRow.join(','));
        });

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `equipamentos_firmware_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns: TableColumn<Equipament>[] = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '100px' },
        { name: 'Apelido', selector: row => row.apelido || '-', sortable: true, width: '100px' },
        { name: 'TID', selector: row => row.tid, sortable: true, width: '100px' },
        { name: 'Tipo', selector: row => row.tipo, sortable: true, width: '120px' },
        { name: 'Setor', selector: row => row.setor, sortable: true, width: '150px' },
        { name: 'Último Heartbeat', selector: row => heartbeats[row.id] || 'Sem Registro', sortable: true, width: '160px', sortFunction: (rowA, rowB) => {
            const timeA = heartbeats[rowA.id] ? new Date(heartbeats[rowA.id]).getTime() : 0;
            const timeB = heartbeats[rowB.id] ? new Date(heartbeats[rowB.id]).getTime() : 0;
            return timeA - timeB;
        }, cell: row => {
            const hb = heartbeats[row.id];
            if (!hb) return <Typography variant="body2">Sem Registro</Typography>;
            
            const d = new Date(hb);
            if (isNaN(d.getTime())) return <Typography variant="body2">{hb}</Typography>;

            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = String(d.getFullYear()).slice(-2);
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const formatted = `${day}/${month}/${year} ${hours}:${minutes}`;

            return <Typography variant="body2">{formatted}</Typography>;
        }},
        { name: 'Firmware Atual', selector: row => row.actual_firmware_version || '-', sortable: true, minWidth: '240px', wrap: true },
        {
            name: 'Ultimo Request',
            selector: row => row.last_fw_request_time ? new Date(row.last_fw_request_time * 1000).toLocaleString() : '-',
            sortable: true,
            minWidth: '180px'
        },
        {
            name: 'Status',
            selector: row => {
                // If it successfully updated and checked in
                if (row.actual_firmware_version && row.firmware_version && row.actual_firmware_version === row.firmware_version) {
                    return 'OK (Updated)';
                }

                let status = row.last_fw_request_status;
                if (status === 'DOWNLOADING') {
                    if (progress[row.id] !== undefined) {
                        status = `DOWNLOADING ${progress[row.id]}%`;
                    }
                    const now = Math.floor(Date.now() / 1000);
                    // If stuck in downloading for more than 10 minutes (600 seconds)
                    if (row.last_fw_request_time && (now - row.last_fw_request_time > 600)) {
                        return 'FAILED (Timeout)';
                    }
                }
                return status || '-';
            },
            sortable: true,
            minWidth: '180px',
            wrap: true
        },
        {
            name: 'Firmware Atribuído',
            cell: (row) => {
                // Determine current value: changed value > existing value > empty
                const currentValue = changes[row.id] !== undefined ? changes[row.id] : (row.firmware_version || "");

                return (
                    <Select
                        size="small"
                        fullWidth
                        value={currentValue}
                        onChange={(e) => handleFirmwareChange(row.id, e.target.value)}
                        displayEmpty
                        variant="outlined"
                        sx={{ fontSize: '0.875rem', height: 35 }}
                    >
                        <MenuItem value="">
                            <em>Nenhum</em>
                        </MenuItem>
                        {firmwares.map(f => (
                            <MenuItem key={f.filename} value={f.filename}>
                                {f.filename} {f.build_date && f.build_time ? ` - ${f.build_date} ${f.build_time} ` : ' '}({Math.round(f.size_bytes / 1024)} KB)
                            </MenuItem>
                        ))}
                    </Select>
                );
            },
            minWidth: '300px'
        },
        {
            name: 'Ações',
            cell: (row) => (
                <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() => handleForceUpdate(row.id)}
                    disabled={saving}
                >
                    Forçar Update
                </Button>
            ),
            width: '140px'
        },
    ];

    const theme = useTheme();

    return (
        <DrawerHeader title="Gerenciamento de Firmware">
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <Paper
                        elevation={2}
                        sx={{
                            p: { xs: 2.5, md: 3 },
                            bgcolor: 'background.paper',
                            borderLeft: `6px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                            mb: 1
                        }}
                    >
                        <Box>
                            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontWeight: 600 }}>
                                SISTEMA E MANUTENÇÃO
                            </Typography>
                            <Typography variant="h4" fontFamily="Saira" fontWeight={900} color="text.primary">Atribuição de Firmware</Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                                Faça o upload de novas versões do sistema operacional dos equipamentos de telemetria e gerencie as atualizações via OTA (Over-The-Air) para máquinas específicas ou lotes completos.
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
            {!dbReady ? (
                <Alert severity="warning">Selecione uma operação (DB).</Alert>
            ) : (
                <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{success}</Alert>}

                    {/* Upload Section */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Upload de Firmware (.bin)</Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Input
                                    type="file"
                                    inputProps={{ accept: ".bin" }}
                                    onChange={(e: any) => setSelectedFile(e.target.files?.[0] || null)}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    disabled={!selectedFile || selectedFile.name !== 'telemetry.bin' || uploading}
                                    onClick={handleFileUpload}
                                >
                                    {uploading ? "Enviando..." : "Enviar Firmware"}
                                </Button>
                                {uploadSuccess && <Typography color="success.main" variant="body2">{uploadSuccess}</Typography>}
                            </Stack>
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                Apenas arquivos com o nome <strong>telemetry.bin</strong> são aceitos.
                            </Typography>
                        </CardContent>
                    </Card>

                    {/* Assignment Section */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Atribuição aos Equipamentos</Typography>

                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 2 }}>
                                <TextField
                                    select
                                    label="Filtrar Setor"
                                    value={selectedSector}
                                    onChange={(e) => setSelectedSector(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                    size="small"
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {sectors.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>

                                <TextField
                                    select
                                    label="Filtrar Tipo"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                    size="small"
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {Array.from(new Set(equipaments.map(e => e.tipo).filter(Boolean))).sort().map(t => (
                                        <MenuItem key={t} value={t}>{t}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    select
                                    label="Filtrar Firmware"
                                    value={firmwareFilter}
                                    onChange={(e) => setFirmwareFilter(e.target.value)}
                                    sx={{ minWidth: 150 }}
                                    size="small"
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {Array.from(new Set(equipaments.map(e => e.actual_firmware_version).filter(Boolean))).sort().map(f => (
                                        <MenuItem key={f} value={f}>{f}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    label="Filtrar ID"
                                    value={idFilter}
                                    onChange={(e) => setIdFilter(e.target.value)}
                                    sx={{ minWidth: 120 }}
                                    size="small"
                                />

                                <TextField
                                    label="Filtrar Apelido"
                                    value={apelidoFilter}
                                    onChange={(e) => setApelidoFilter(e.target.value)}
                                    sx={{ minWidth: 120 }}
                                    size="small"
                                />

                                <TextField
                                    label="Filtrar TID"
                                    value={tidFilter}
                                    onChange={(e) => setTidFilter(e.target.value)}
                                    sx={{ minWidth: 120 }}
                                    size="small"
                                />

                                <Box flexGrow={1} />

                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={handleClearStatus}
                                    disabled={saving || loading}
                                >
                                    Limpar Status
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleRefreshFirmwares}
                                    disabled={saving || loading}
                                >
                                    Atualizar Versões Atuais (Sync)
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveChanges}
                                    disabled={saving || Object.keys(changes).length === 0}
                                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                                </Button>
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography variant="subtitle2">Aplicar a todos os listados:</Typography>
                                <Select
                                    size="small"
                                    value={bulkFirmwareVersion}
                                    onChange={(e) => setBulkFirmwareVersion(e.target.value)}
                                    displayEmpty
                                    sx={{ minWidth: 250 }}
                                >
                                    <MenuItem value="">
                                        <em>Nenhum</em>
                                    </MenuItem>
                                    {firmwares.map(f => (
                                        <MenuItem key={f.filename} value={f.filename}>
                                            {f.filename} {f.build_date && f.build_time ? ` - ${f.build_date} ${f.build_time} ` : ' '}({Math.round(f.size_bytes / 1024)} KB)
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Button
                                    variant="outlined"
                                    onClick={handleBulkApply}
                                    disabled={filteredEquipaments.length === 0}
                                >
                                    Aplicar Agora
                                </Button>
                            </Stack>

                            <Box mt={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="body2" color="textSecondary">
                                        Total: {filteredEquipaments.length} equipamentos
                                    </Typography>
                                    <Button variant="outlined" size="small" onClick={exportToCSV}>
                                        Exportar CSV
                                    </Button>
                                </Stack>
                                <DataTable
                                    columns={columns}
                                    data={filteredEquipaments}
                                    pagination
                                    paginationPerPage={100}
                                    paginationRowsPerPageOptions={[10, 50, 100, 1000]}
                                    progressPending={loading}
                                    persistTableHead
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Stack>
            )}
        </DrawerHeader>
    );
}
